import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { PersonFormInterface } from '@zaoblako/shared/modules/client/dtos/person/person-form.dto';
import type { PersonPassportDataInterface } from '@zaoblako/shared/modules/client/dtos/person/person-passport-data.dto';
import { PersonTypeEnum } from '@zaoblako/shared/modules/client/enums/person.type.enum';
import type { TaskContragentBankDetailsFormInterface } from '@zaoblako/shared/modules/task/dtos/contragent/task-contragent-bank-details-form.dto';
import { TaskContragentFormInterface } from '@zaoblako/shared/modules/task/dtos/contragent/task-contragent-form.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConditionalRouterLinkDirective } from '@zaoblako/web/shared/directives/conditional-router-link.directive';
import { BankAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-auto-complete/bank-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { InnAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/inn-auto-complete/inn-auto-complete.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ParseErrorFormPipe } from '@zaoblako/web/shared/pipes/parse-error-form.pipe';
import { PersonTypeIsNaturalPipe } from '@zaoblako/web/shared/pipes/person-type-is-natural.pipe';
import { PersonTypeNamePipe } from '@zaoblako/web/shared/pipes/person-type-name.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';

@Component({
  standalone: true,
  selector: 'crm-card-contragent-info',
  templateUrl: './card-contragent-info.component.html',
  imports: [
    PersonTypeNamePipe,
    PersonTypeIsNaturalPipe,
    CommonModule,
    CardModule,
    ContragentAutoCompleteComponent,
    JoinPipe,
    DividerModule,
    ButtonModule,
    InputTextComponent,
    SingleSelectComponent,
    InnAutoCompleteComponent,
    CheckboxModule,
    FormsModule,
    DatepickerComponent,
    BankAutoCompleteComponent,
    ParseErrorFormPipe,
    TableComponent,
    ConditionalRouterLinkDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardContragentInfoComponent {
  @Input() public contragent: TaskContragentFormInterface;
  @Output() public contragentChange = new EventEmitter<TaskContragentFormInterface>();

  public passportShow = false;

  @Input() public banks: BankFindInterface[] = [];
  @Input() public editable = true;

  @Input() public errors: ValidationErrorInterface<TaskContragentFormInterface>;
  @Input() public styleClass: string;
  @Input() public appendTo: string;

  @Output() public bankSearch = new EventEmitter<string>();

  public personTypes: PersonTypeEnum[] = Object.values(PersonTypeEnum);

  public persons: PersonFormInterface[] = [];

  public columns: CrmTableColumn[] = [{}, { label: 'Банк' }, { label: 'Расчетный счёт' }, {}];

  public constructor(
    private readonly contragentService: ContragentService,
    private readonly companyService: CompanyService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public createContragent() {
    this.contragent = <TaskContragentFormInterface>{};
    this.contragent.passport = <PersonPassportDataInterface>{};
    this.contragentChange.emit(this.contragent);
  }

  public clearContragent() {
    this.contragent = null;
    this.errors = null;
    this.contragentChange.emit(this.contragent);
    this.passportShow = false;
  }

  public contragentSelect(contragent: ContragentFindInterface) {
    this.contragent = contragent;
    this.errors = null;
    this.contragentChange.emit(this.contragent);

    if (this.contragent?.bankDetails?.length) {
      this.contragent.bankDetails[0].isSelected = true;
      this.cdRef.markForCheck();
    } else {
      if (this.contragent?.id) {
        this.contragentService.bankDetailsFrom1C(this.contragent?.id).subscribe((data) => {
          if (data?.length) {
            this.contragent.bankDetails = data as any;
            this.contragent.bankDetails[0].isSelected = true;
            this.cdRef.markForCheck();
          }
        });
      }
    }
  }

  public searchParson(name: string) {
    this.companyService.externalSearch({ query: name }).subscribe((persons) => {
      this.persons = persons;
      this.cdRef.markForCheck();
    });
  }

  public selectedPerson(person: PersonFormInterface) {
    if (person) {
      if (typeof person === 'object') {
        this.contragent.name = person.name;
        this.contragent.personType = person.personType;
        this.contragent.kpp = person.kpp;
        this.contragent.inn = person.inn;
      } else {
        this.contragent.inn = person;
      }
      this.cdRef.markForCheck();
    }
  }

  public toBankDetail(bankDetail: any): TaskContragentBankDetailsFormInterface {
    return bankDetail as TaskContragentBankDetailsFormInterface;
  }

  public toNumber(val: any): number {
    return val as number;
  }

  public addBankDetail() {
    if (!this.contragent.bankDetails) {
      this.contragent.bankDetails = [];
    }
    this.contragent.bankDetails.push(<TaskContragentBankDetailsFormInterface>{});
    if (this.contragent.bankDetails?.length === 1) {
      this.contragent.bankDetails[0].isSelected = true;
    }
    this.contragent.bankDetails = [...this.contragent.bankDetails];
  }

  public deleteBankDetail(bankDetail: TaskContragentBankDetailsFormInterface) {
    this.contragent.bankDetails = this.contragent.bankDetails.filter((_bankDetail) => _bankDetail != bankDetail);
  }

  public setSelectedBankDetail(bankDetail: TaskContragentBankDetailsFormInterface) {
    if (bankDetail.isSelected) {
      this.contragent.bankDetails.forEach((_bankDetail) => (_bankDetail.isSelected = false));
      bankDetail.isSelected = true;
    }
  }
}
